import { useCallback, useEffect, useState } from "react";
import type { LoadResult } from "@cuekards/core/script-source";
import type { RecordingStatus } from "@cuekards/core/recording";
import { IDLE_STATUS, isRecordingActive } from "@cuekards/core/recording";
import { buildScriptModel } from "@cuekards/core/script-model";
import {
  IDLE_VOICE_COMMANDS,
  type VoiceCommandState,
} from "@cuekards/core/voice-commands";
import { PrompterPane } from "./PrompterPane.js";
import { RecoveryPanel } from "./RecoveryPanel.js";
import { SettingsModal } from "./SettingsModal.js";
import {
  SetupPane,
  type SetupPaneProps,
  type SetupStatus,
} from "./SetupPane.js";
import { StopConfirmPrompt } from "./prompter-chrome.js";
import { Transport } from "./Transport.js";
import { useCapture, type AudioTap } from "./use-capture.js";
import { useShortcuts } from "./use-shortcuts.js";

/**
 * The desktop's one window (one-window ticket 05).
 *
 * It replaces `ControlApp` and the overlay window together.
 *
 * **One screen and a bottom bar** (one-window-polish ticket 05). It was three
 * tabs taking turns at being the window, and that was the shape of the previous
 * effort's compromise rather than the shape of the work: in use only one of the
 * three is ever the window. The prompter is what you look at while filming, so it
 * *is* the window; setup is what you do once, before there is anything to look
 * at, so it is the same space's empty state; settings is somewhere you go and
 * come back from, so it is a modal over the top. The body is therefore one thing
 * chosen by one fact — is a script loaded — and the row of controls under it is
 * the only chrome left.
 *
 * What still sits in the shell rather than in the body is what has to stay
 * reachable whatever is being shown, which is the rule `ControlApp` already
 * stated about the transport: *a stop you have to go and find a tab for is not a
 * stop.* That is the recovery offer, the spoken-stop prompt and the bottom bar.
 *
 * The spoken-stop prompt is the newest of the three and the clearest case. It is
 * a question with a five-second deadline and no way to answer it except out loud;
 * drawn inside the prompter it would be invisible to anyone whose own voice ended
 * the take while the setup form was up.
 *
 * **A successful load shows the prompter, and nothing here makes it happen.** The
 * prompter *is* the proof a load worked now that the raw-text preview is gone —
 * and it is what the window shows whenever a script is loaded, so that stopped
 * being a rule the shell enforces and became a consequence of the structure. The
 * one line left is about the *modal*: a load from its Script tab closes it, so
 * that the answer to "did that work?" is not behind the screen you asked for it
 * from. A *refresh* closes nothing: it is the same document, and being thrown out
 * of the settings by a button that re-fetches would be a surprise.
 */
export function AppShell({ tapAudio }: { tapAudio: AudioTap }): JSX.Element {
  const [url, setUrl] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [status, setStatus] = useState<SetupStatus>({ kind: "idle" });
  const [recording, setRecording] = useState<RecordingStatus>(IDLE_STATUS);
  const [voice, setVoice] = useState<VoiceCommandState>(IDLE_VOICE_COMMANDS);
  const [refreshing, setRefreshing] = useState(false);

  // This window owns the camera *and* the microphone: `getUserMedia` and
  // `MediaRecorder` are web APIs, so the capture half of the recording engine
  // lives here and takes its orders from the host (ticket 12) — and it is the
  // single place the microphone is opened, then forked to the follower (ticket
  // 14, FR-31). The fork itself is the host's, which is why it arrives as a
  // prop: see {@link AudioTap}. It is mounted on the shell rather than on the
  // body so that what the window is showing mid-take cannot unmount a running
  // recorder.
  useCapture(tapAudio);

  const load = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    setStatus({ kind: "loading" });
    const result: LoadResult = await window.cuekards.loadScript(trimmed);
    setStatus(toStatus(result));
    // The prompter is behind the modal the moment the status says loaded, so
    // getting out of the way is the whole of "a successful load shows it". A no-op
    // on the launch path and from the window's own setup form, where nothing is
    // open to close.
    if (result.ok) setSettingsOpen(false);
  }, []);

  // Re-fetch the loaded doc, keeping the reader's place (FR-16). Position
  // re-anchors behind the contract; here we just refresh what the setup pane
  // says arrived.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    const result = await window.cuekards.refreshScript();
    setRefreshing(false);
    if (result) setStatus(toStatus(result));
  }, []);

  // On launch, prefill and reload the last-used URL (FR-18).
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getLastDocUrl().then((last) => {
      if (cancelled || !last) return;
      setUrl(last);
      void load(last);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // The keyboard (one-window ticket 06, FR-52). On the shell rather than on the
  // prompter pane, so `Space` still pauses a take from the settings screen — the
  // same rule the transport is up here for. Nothing is registered with Windows,
  // so these fire while this window is focused and are somebody else's keys the
  // rest of the time.
  useShortcuts("desktop", {
    // Straight to the same call the setup pane's button makes, so the key and the
    // button cannot disagree — and main refuses it mid-take either way (FR-17).
    refresh: () => void refresh(),
  });

  // Follow the recording status: it drives the transport, and greys refresh and
  // the device picker for the duration of a take (FR-17).
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getRecordingStatus().then((s) => {
      if (!cancelled) setRecording(s);
    });
    const off = window.cuekards.onRecordingStatusChanged(setRecording);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  // And the voice-command state, for the one part of it the shell draws: a stop
  // that was heard and is waiting to be confirmed (FR-56).
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getVoiceCommandState().then((s) => {
      if (!cancelled) setVoice(s);
    });
    const off = window.cuekards.onVoiceCommandStateChanged(setVoice);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  const takeInProgress = isRecordingActive(recording.phase);

  // Built once and handed to both places the form appears, which is what makes
  // "the same setup pane" a fact about this object rather than a rule two call
  // sites have to keep.
  const setup: SetupPaneProps = {
    url,
    onUrlChange: setUrl,
    onLoad: () => void load(url),
    onRefresh: () => void refresh(),
    status,
    refreshing,
    takeInProgress,
  };

  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  return (
    <main className="app-shell">
      {/* Interrupted takes (ticket 13, FR-47). First, because on the launch where
        * it appears it is the most important thing on the screen — footage that
        * cannot be reshot is waiting to be turned into a file. Absent otherwise.
        * Locked mid-take for the same reason refresh is: ffmpeg and a live
        * capture should not be fighting over the same disk. */}
      <RecoveryPanel disabled={takeInProgress} />

      {/* A spoken stop waiting for its confirmation (FR-56). Above the body
        * because it outranks whatever is in it for the five seconds it is up. */}
      {voice.awaitingConfirmation && <StopConfirmPrompt />}

      {/* The one growing child of the window, and what the settings cover when
        * they are open — the modal is positioned against this rather than against
        * the viewport, which is what keeps the bottom bar out from under it. That
        * is the same rule the bar is down there for: a stop you cannot press is
        * not a stop, and a gear you cannot press again is not a toggle. */}
      <div className="app-body">
        {/* One fact chooses what the window shows. There is no third option and
          * no way to ask for one: the form is what an empty window looks like,
          * not a place you navigate to, and once a script is loaded the way back
          * to the URL box is the settings modal's Script tab — the same
          * component, from the same props.
          *
          * `.app-pane` is what the prompter's height comes from, since the height
          * it is given is what the reading line is anchored against. */}
        <div className="app-pane">
          {status.kind === "loaded" ? <PrompterPane /> : <SetupPane {...setup} />}
        </div>

        {/* The settings screens (ticket 16, FR-61 to FR-64) — the document, the
          * prompter's defaults, capture devices, the shortcuts and voice
          * commands. Over the top rather than beside, because none of it is what
          * you reach for while filming and all of it is somewhere you go and come
          * back from. */}
        {settingsOpen && (
          <SettingsModal
            onClose={closeSettings}
            disabled={takeInProgress}
            setup={setup}
          />
        )}
      </div>

      {/* The bottom bar (ticket 12, FR-43). Under the body and outside it: during
        * a take it is the only thing in this window that matters. */}
      <Transport
        status={recording}
        onOpenSettings={() => setSettingsOpen((open) => !open)}
      />
    </main>
  );
}

function toStatus(result: LoadResult): SetupStatus {
  return result.ok
    ? {
        kind: "loaded",
        docId: result.docId,
        // Built here rather than kept as raw text: the setup pane says how much
        // arrived, and this is the same model the prompter is drawn from, so the
        // two cannot disagree about what "loaded" meant.
        model: buildScriptModel(result.text),
      }
    : { kind: "error", reason: result.reason, message: result.message };
}
